import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { useState } from 'react';
import { feedback } from 'react-feedbacker';
import { useForm } from 'react-hook-form';
import { useMutation } from 'urql';

import { useFeedbackMessages } from '@/bits';
import {
  Card,
  CardBody,
  CheckboxField,
  CountryField,
  DateField,
  EmailField,
  ErrorMessage,
  NakedForm,
  PhoneNumberField,
  SelectField,
  SubmitButton,
  TextField,
} from '@/components';
import { useTranslate } from '@/contexts';
import { BrandEnum, CloseAccountCauseV4 } from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { useBrandValueOptions, useBrands } from '@/hooks/useBrands';
import { Nullable } from '@/types';
import { useCan } from '@/utils/access';
import formatDate from '@/utils/formatter/formatDate';
import {
  CreateTestPlayerMutation,
  CreateTestPlayerMutationVariables,
} from './__generated__/component';

const BLOCK_STATIC_QUERY = graphql`
  query SanityCreateTestPlayerBlock {
    sanityCreateTestPlayerBlock {
      title {
        ...SanityLocaleString
      }
    }
  }
`;

const CREATE_TEST_PLAYER = gql`
  mutation CreateTestPlayer(
    $phoneNumber: PhoneNumber!
    $birthDate: LocalDate!
    $email: EmailAddress!
    $firstName: String!
    $lastName: String!
    $middleName: String
    $street: String!
    $zipCode: String!
    $city: String!
    $district: String
    $state: String
    $countryCode: String!
    $brand: BrandEnum!
    $isTestAccount: Boolean
    $bypassCountryValidation: Boolean
    $bypassAgeValidation: Boolean
    $closeCause: CloseAccountCauseV4
  ) {
    createPlayerV4(
      phoneNumber: $phoneNumber
      birthDate: $birthDate
      email: $email
      firstName: $firstName
      lastName: $lastName
      middleName: $middleName
      street: $street
      zipCode: $zipCode
      city: $city
      district: $district
      state: $state
      countryCode: $countryCode
      brand: $brand
      isTestAccount: $isTestAccount
      bypassCountryValidation: $bypassCountryValidation
      bypassAgeValidation: $bypassAgeValidation
      closeCause: $closeCause
    ) {
      id
    }
  }
`;

type FormValues = {
  phoneNumber: string;
  birthDate: string;
  email: string;
  firstName: string;
  lastName: string;
  street: string;
  zipCode: string;
  city: string;
  countryCode: string;
  brand: BrandEnum | '';
  isTestAccount: boolean;
  bypassCountryValidation: boolean;
  createUnderage: boolean;
};

const CreateTestPlayerBlock = () => {
  const { t } = useTranslate();
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const feedbackMessages = useFeedbackMessages();
  const isMounted = useIsMounted();
  const canBypassCountryValidation = useCan('BYPASS_COUNTRY_VALIDATION');
  const canCreateUnderagePlayer = useCan('CREATE_UNDERAGE_PLAYER');
  const { brands } = useBrands();
  const { options: brandOptions } = useBrandValueOptions(brands);

  const [, createTestPlayer] = useMutation<
    CreateTestPlayerMutation,
    CreateTestPlayerMutationVariables
  >(CREATE_TEST_PLAYER);

  const block =
    useStaticQuery<Queries.SanityCreateTestPlayerBlockQuery>(
      BLOCK_STATIC_QUERY,
    ).sanityCreateTestPlayerBlock;

  const defaultValues: FormValues = {
    birthDate: '',
    brand: '',
    bypassCountryValidation: false,
    city: '',
    createUnderage: false,
    isTestAccount: false,
    countryCode: '',
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    street: '',
    zipCode: '',
  };

  const methods = useForm({
    defaultValues,
  });

  const onSubmit = ({ brand, createUnderage, ...values }: FormValues) => {
    if (!brand) {
      return;
    }

    setErrorMessage(null);

    createTestPlayer({
      ...values,
      brand,
      birthDate: formatDate(values.birthDate, true),
      bypassAgeValidation: createUnderage,
      closeCause: createUnderage ? CloseAccountCauseV4.ResponsibleGaming : null,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      }
      if (res.data?.createPlayerV4.id && isMounted) {
        feedback.success(t(feedbackMessages.success));
        methods.reset();
      }
    });
  };

  if (!block) {
    return null;
  }

  return (
    <Card size="lg" title={t(block.title)}>
      <CardBody>
        <div className="p-3">
          <NakedForm
            methods={methods}
            onSubmit={onSubmit}
            className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
          >
            <EmailField
              name="email"
              id="create-test-player-block__email"
              title="Email"
              required
            />
            <PhoneNumberField
              name="phoneNumber"
              id="create-test-player-block__phoneNumber"
              title="Phone Number"
              required
            />
            <TextField
              name="firstName"
              id="create-test-player-block__firstName"
              title="First Name"
              required
            />
            <TextField
              name="lastName"
              id="create-test-player-block__lastName"
              title="Last Name"
              required
            />
            <DateField
              name="birthDate"
              id="create-test-player-block__birthDate"
              title="Date of Birth"
              showYearDropdown
              showMonthDropdown
              required
            />
            <TextField
              name="city"
              id="create-test-player-block__city"
              title="City"
              required
            />
            <TextField
              name="street"
              id="create-test-player-block__street"
              title="Street"
              required
            />
            <CountryField
              name="countryCode"
              id="create-test-player-block__countryCode"
              title="Country"
              required
            />
            <TextField
              name="zipCode"
              id="create-test-player-block__zipCode"
              title="Zip Code"
              required
            />
            <SelectField
              name="brand"
              id="create-test-player-block__brand"
              title="Brand"
              options={brandOptions}
              required
            />
            <details className="col-span-full border border-gray-200 dark:border-gray-700 rounded-md">
              <summary className="cursor-pointer px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 select-none">
                Advanced settings
              </summary>
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 grid gap-3">
                {canBypassCountryValidation && (
                  <CheckboxField
                    name="bypassCountryValidation"
                    title="Bypass Country Validation"
                  />
                )}
                <CheckboxField name="isTestAccount" title="Test account" />
                {canCreateUnderagePlayer && (
                  <CheckboxField
                    name="createUnderage"
                    title="Create underage player (account will be closed under Responsible Gaming)"
                  />
                )}
              </div>
            </details>
            <ErrorMessage message={errorMessage} />
            <SubmitButton value="Create Player" />
          </NakedForm>
        </div>
      </CardBody>
    </Card>
  );
};

export default CreateTestPlayerBlock;
